/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignments Container
 *
 * Page-level export surface for assignment setup flows used by parent apps
 * (add manually, inline edit, quick schedule, questions/topics review).
 *
 * @file AssignmentsContainer.jsx
 * @author Gladson Samuel S
 * @since 2.0.3
 */

import React from 'react';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';
import { InternationalSupport } from '@aquila/core';

import ContainerShell from './ContainerShell';
import ErrorBoundary from '../components/common/ErrorBoundary';
import msgObject from '../../translations';

import InlineActionAlert from '../components/common/InlineActionAlert';
import QuestionTopicsModal from '../components/assignmentModal/QuestionTopicsModal';

export {
  AssignmentsLayout,
  AssignmentForm,
  AssignmentList,
  AssignmentListItem,
  AssignmentDueDateModalProvider,
  AssignmentSettingsForm,
  GradebookInfoModal,
  GradebookInfoModalWithStyles,
  assignmentSettingsFormDefaultConfig,
  mergeAssignmentSettingsConfig,
  resolveAssignmentSettingsVisibility,
  getVisibleSections,
  GRADEBOOK_DISPLAY_MODE,
  LATE_WORK_DEADLINE_TYPE,
  ASSIGNMENT_AVAILABILITY,
  EMPTY_ASSIGNMENT_SETTINGS_VALUES,
  buildAssignmentSettingsFormDefaultValues
} from '../components/assignments';

export {
  InlineActionAlert,
  QuestionTopicsModal
};

export {
  selectQuestionTopicsModalSection,
  getDefaultQuestionTopicsSectionId
} from '../components/assignmentModal/questionTopicsModalUtils';

const AssignmentsContainer = ({ locale, children }) => {
  const intlObj = new InternationalSupport(msgObject, locale);

  return (
    <ContainerShell>
      <IntlProvider locale={intlObj.getLocale()} messages={intlObj.getMessages()}>
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </IntlProvider>
    </ContainerShell>
  );
};

AssignmentsContainer.propTypes = {
  locale: PropTypes.string,
  children: PropTypes.node.isRequired
};

AssignmentsContainer.defaultProps = {
  locale: 'en'
};

export default AssignmentsContainer;
